import React, { useState, useMemo, useEffect } from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationCircle } from '@fortawesome/pro-solid-svg-icons/faExclamationCircle';
import classnames from 'classnames';

import { FormattedMessage } from 'react-intl';
import { Button } from 'ui-components/components/button';
import { CurrencyInput } from 'ui-components/components/currency-input';
import { noop } from 'ui-components/default-props/noop';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import messages from './messages';
import styles from './style.module.less';
import FormattedCurrencyContainer from '../../../../../components/formatted-currency/container';

import { useFundLimitRemaining } from '../../../../../hooks/account-information/use-fund-limit-remaining';
import { MINIMUM_PURCHASE_AMT, ALL_FUND_AMTS } from '../constants/add-funds-constants';

const MINIMUM_NUMBER_OF_BUTTONS = 4;

const calculateStartingButtonIndex = (values, minimumPurchaseAmount) => {
  if (values.length === MINIMUM_NUMBER_OF_BUTTONS) {
    return 0;
  }
  let low = 0;
  let high = values.length - 1;
  let mid;
  while (low < high) {
    mid = Math.floor((low + high) / 2);
    if (values[mid] < minimumPurchaseAmount + 1) {
      low = mid + 1;
    } else {
      high = mid;
    }
  }
  return low;
};

/**
 * @param {object} props
 * @param {(value: number) => void} props.onClickAction
 * @param {string} props.sellerCurrencyCode
 * @param {number[]} [props.values]
 * @param {number} [props.fundAmount]
 * @param {boolean} [props.disabled]
 * @param {(value: number) => void} [props.onChangeAction]
 * @param {string} [props.classNames]
 * @param {number} [props.minimumAmount]
 * @param {(visible: boolean) => void} [props.onOtherButtonVisibilityChange]
 * @returns {JSX.Element}
 */
export const AddFundButtons = ({
  values = ALL_FUND_AMTS,
  onClickAction,
  sellerCurrencyCode,
  fundAmount = 0,
  disabled = false,
  onChangeAction = noop,
  classNames = '',
  minimumAmount = MINIMUM_PURCHASE_AMT,
  onOtherButtonVisibilityChange = noop,
}) => {
  const minimumPurchaseAmount = useMemo(
    () => (minimumAmount < MINIMUM_PURCHASE_AMT ? MINIMUM_PURCHASE_AMT : minimumAmount),
    [minimumAmount],
  );

  const initialStartingIndex = calculateStartingButtonIndex(values, minimumPurchaseAmount);
  const initialAmount = fundAmount > values[initialStartingIndex] ? fundAmount : values[initialStartingIndex];
  const isOtherValue = !values.includes(initialAmount);

  const [otherAmount, setOtherStartAmout] = useState(fundAmount < minimumAmount ? Math.ceil(minimumAmount) : fundAmount);
  const isBelowMinimumValue = useMemo(() => otherAmount < minimumPurchaseAmount, [otherAmount, minimumPurchaseAmount]);
  const [otherButtonActive, setOtherButtonActive] = useState(isOtherValue);

  const limitRemaining = useFundLimitRemaining();
  const isLimitRemainingLessThanMinimum = useMemo(() => limitRemaining < minimumPurchaseAmount, [limitRemaining, minimumPurchaseAmount]);

  // adjusts the amount of the first button in the array.  Used in first label flow.  ex. if label total is $57, then first button in array will be $100
  const startingButtonValue = useMemo(() => {
    return calculateStartingButtonIndex(values, minimumPurchaseAmount);
  }, [minimumPurchaseAmount, values]);
  const responsiveButtonValues = useMemo(() => {
    const index = startingButtonValue;
    return values.slice(index, index + 4);
  }, [startingButtonValue, values]);

  const adjustedFundAmount = useMemo(() => {
    const index = startingButtonValue;
    let amount = values[index];
    // if fundAmount ( defaultFunding amount or default auto funding amount) is higher that the starting button value determined in startingButtonValue, it will set the value to that amount
    if (fundAmount > amount) {
      amount = fundAmount;
    }
    return amount;
  }, [fundAmount, startingButtonValue, values]);

  // Handle side effects for setting "Other" button state based on adjusted amount
  useEffect(() => {
    // checks to see if the adjusted amount is an 'Other' amount
    if (!values.includes(adjustedFundAmount)) {
      setOtherButtonActive(true);
      setOtherStartAmout(adjustedFundAmount);
    }
    // Note: hiding the "Other" input when clicking preset buttons is handled in onClickValueButtons
  }, [adjustedFundAmount, values]);

  const initialSelectedValue = useMemo(() => {
    if (isLimitRemainingLessThanMinimum) {
      return undefined;
    }
    const adjusted = adjustedFundAmount;
    // If the adjusted amount exceeds the limit, find the highest valid value that doesn't
    if (adjusted > limitRemaining) {
      const validValues = responsiveButtonValues.filter((val) => val <= limitRemaining);
      if (validValues.length > 0) {
        return validValues[validValues.length - 1]; // Return the highest valid value
      }
      return undefined;
    }
    return adjusted;
  }, [isLimitRemainingLessThanMinimum, adjustedFundAmount, limitRemaining, responsiveButtonValues]);

  const [selectedValue, setSelectedValue] = useState(initialSelectedValue);

  const onChangeOtherInput = (value) => {
    let numberValue = Number(value);
    if (Number.isNaN(numberValue)) {
      numberValue = 0;
    }
    setOtherStartAmout(numberValue);
    onChangeAction(numberValue);
  };

  const onClickOtherButton = () => {
    setOtherButtonActive(true);
    onOtherButtonVisibilityChange(true);
    onClickAction(otherAmount);
  };

  const onClickValueButtons = (value) => {
    setOtherButtonActive(false);
    onOtherButtonVisibilityChange(false);
    setSelectedValue(value);
    onClickAction(value);
  };

  return (
    <>
      <div className={styles['button-display']}>
        {responsiveButtonValues.map((value) => {
          const isSelectedValue = selectedValue === value;
          const isDisabled = value > limitRemaining || isLimitRemainingLessThanMinimum;
          return (
            <Button
              key={value}
              className={classnames({
                [styles['amount-button-selected']]: isSelectedValue && !otherButtonActive && !disabled,
              })}
              disabled={disabled || isDisabled}
              onClick={() => onClickValueButtons(value)}
              theme={THEME_CONSTANTS.unstyled}
            >
              <FormattedCurrencyContainer currencyCode={sellerCurrencyCode} fractionDigits={0} value={value} />
            </Button>
          );
        })}
        <Button
          className={styles['other-button']}
          disabled={disabled || isLimitRemainingLessThanMinimum}
          onClick={onClickOtherButton}
          theme={THEME_CONSTANTS.link}
        >
          <FormattedMessage {...messages.otherButtonText} />
        </Button>
      </div>
      {otherButtonActive && !disabled ? (
        <div className={styles['input-margin']}>
          <CurrencyInput
            className={classNames}
            currencyCode={sellerCurrencyCode}
            disabled={disabled}
            onChange={onChangeOtherInput}
            value={otherAmount}
          />
          <div
            className={classnames(styles['amount-note-text'], {
              [styles['amount-error']]: isBelowMinimumValue,
            })}
          >
            {isBelowMinimumValue ? <FontAwesomeIcon className={styles['error-icon']} icon={faExclamationCircle} /> : null}
            <FormattedMessage
              {...messages.minimumFundsToAdd}
              values={{
                minimum: <FormattedCurrencyContainer currencyCode={sellerCurrencyCode} fractionDigits={0} value={minimumPurchaseAmount} />,
              }}
            />
          </div>
        </div>
      ) : null}
    </>
  );
};
